import React, { useState, useEffect, useCallback } from "react";
import { Spinner, Alert, Button, Form } from "reactstrap";
import {
  getBusinessConfig,
  createBusinessConfig,
  updateBusinessConfig,
  uploadPurchaseRulesDocument,
} from "../../ApiCalls/businessConfiguration";
import "./styles/businessConfigTab.scss";

function BusinessConfigTab({ mantisBusinessId, token }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [enabledSections, setEnabledSections] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [creating, setCreating] = useState(false);

  const availableSections = [
    "BUSINESS_RULES",
    "CATEGORIES",
    "COST_CENTERS",
    "BUDGETS",
    "USERS",
  ];

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getBusinessConfig(token, mantisBusinessId);
      setConfig(data);
      setEnabledSections(data?.enabledSections || []);
    } catch (err) {
      setError(err.message || "Failed to load configuration");
    } finally {
      setLoading(false);
    }
  }, [token, mantisBusinessId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleCreateConfig = async () => {
    try {
      setCreating(true);
      setError("");
      const created = await createBusinessConfig(token, mantisBusinessId);
      setConfig(created);
      setEnabledSections(created?.enabledSections || []);
      setSuccess("Configuration created");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message || "Failed to create configuration");
    } finally {
      setCreating(false);
    }
  };

  const handleSectionToggle = (section) => {
    setEnabledSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section]
    );
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      // Only send mutable fields — _id is immutable and Mongo rejects it in $set.
      const saved = await updateBusinessConfig(token, mantisBusinessId, {
        enabledSections,
      });
      setConfig(saved);
      setEnabledSections(saved?.enabledSections || []);
      setSuccess("Configuration saved successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError("File size exceeds 10MB limit");
      return;
    }

    try {
      setUploadingDocument(true);
      setError("");
      setSuccess("");

      const result = await uploadPurchaseRulesDocument(
        token,
        mantisBusinessId,
        file,
        file.name
      );
      setConfig(result);
      setSuccess("Document uploaded successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message || "Failed to upload document");
    } finally {
      setUploadingDocument(false);
      event.target.value = "";
    }
  };

  const handleDownloadDocument = () => {
    if (config?.purchaseRulesDocument?.base64) {
      const link = document.createElement("a");
      link.href = `data:${config.purchaseRulesDocument.mimeType};base64,${config.purchaseRulesDocument.base64}`;
      link.download = config.purchaseRulesDocument.name || "document";
      link.click();
    }
  };

  if (loading) {
    return (
      <div className="business-config-tab">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="business-config-tab">
      {error && <Alert color="danger">{error}</Alert>}
      {success && <Alert color="success">{success}</Alert>}

      {config ? (
        <div className="config-container">
          <div className="config-header">
            <div>
              <h5 className="config-title">Configuración del negocio</h5>
              <span className="config-subtitle">
                Última actualización:{" "}
                {config.dateUpdated
                  ? new Date(config.dateUpdated).toLocaleDateString()
                  : "—"}
              </span>
            </div>
            <Button
              color="primary"
              size="sm"
              onClick={handleSaveConfig}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner size="sm" /> Guardando...
                </>
              ) : (
                <>
                  <i className="fas fa-save" /> Guardar cambios
                </>
              )}
            </Button>
          </div>

          <div className="config-grid">
            {/* Enabled Sections */}
            <section className="config-panel">
              <header className="panel-header">
                <i className="fas fa-list-check" />
                <div>
                  <h6 className="panel-title">Secciones habilitadas</h6>
                  <span className="panel-hint">
                    {enabledSections.length} de {availableSections.length}{" "}
                    activas
                  </span>
                </div>
              </header>

              <Form className="sections-grid">
                {availableSections.map((section) => (
                  <label
                    className={`section-toggle ${
                      enabledSections.includes(section) ? "is-active" : ""
                    }`}
                    key={section}
                    htmlFor={`section-${section}`}
                  >
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`section-${section}`}
                      checked={enabledSections.includes(section)}
                      onChange={() => handleSectionToggle(section)}
                    />
                    <span>{section.replace(/_/g, " ").toLowerCase()}</span>
                  </label>
                ))}
              </Form>
            </section>

            {/* Purchase Rules Document */}
            <section className="config-panel">
              <header className="panel-header">
                <i className="fas fa-file-contract" />
                <div>
                  <h6 className="panel-title">Documento de reglas de compra</h6>
                  <span className="panel-hint">PDF, DOC, DOCX o TXT</span>
                </div>
              </header>

              {config?.purchaseRulesDocument?.name ? (
                <div className="document-info">
                  <i className="fas fa-file" />
                  <div className="document-details">
                    <p className="document-name">
                      {config.purchaseRulesDocument.name}
                    </p>
                    <small className="text-muted">
                      {config.purchaseRulesDocument.mimeType || "documento"}
                    </small>
                  </div>
                  <Button size="sm" color="info" onClick={handleDownloadDocument}>
                    <i className="fas fa-download" />
                  </Button>
                </div>
              ) : (
                <p className="no-document">Sin documento cargado</p>
              )}

              <input
                type="file"
                id="document-upload"
                onChange={handleDocumentUpload}
                disabled={uploadingDocument}
                accept=".pdf,.doc,.docx,.txt"
                style={{ display: "none" }}
              />
              <label htmlFor="document-upload" className="upload-button">
                {uploadingDocument ? (
                  <>
                    <Spinner size="sm" color="primary" /> Subiendo...
                  </>
                ) : (
                  <>
                    <i className="fas fa-cloud-upload-alt" />
                    {config?.purchaseRulesDocument?.name
                      ? "Reemplazar documento"
                      : "Subir documento"}
                  </>
                )}
              </label>
            </section>
          </div>
        </div>
      ) : (
        <div className="config-empty-state">
          <Alert color="warning">
            This business has no configuration record yet.
          </Alert>
          <Button color="primary" onClick={handleCreateConfig} disabled={creating}>
            {creating ? (
              <>
                <Spinner size="sm" /> Creating...
              </>
            ) : (
              <>
                <i className="fas fa-plus" /> Create configuration
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

export default BusinessConfigTab;
